const sameValue = Object.is ?? ((left, right) =>
    left === right ? left !== 0 || 1 / left === 1 / right : left !== left && right !== right
);

let nextStoreId = 1;

function callListeners(listeners, args) {
    let firstError;

    for (const listener of Array.from(listeners)) {
        if (!listeners.has(listener)) continue;
        try {
            listener(...args);
        } catch (error) {
            firstError ??= error;
        }
    }

    if (firstError) throw firstError;
}

export function createStore(initialValue, updater) {
    if (updater != null && typeof updater !== 'function') {
        throw new TypeError('The updater must be a function when provided.');
    }

    const subscribers = new Set();
    const eventListeners = new Map();
    const destroyListeners = new Set();
    const notificationQueue = [];
    const id = `restate:${nextStoreId++}`;

    let stateValue = initialValue;
    let revision = 0;
    let destroyed = false;
    let updaterRunning = false;
    let updaterCleanup;
    let manuallyStarted = false;
    let eventTarget;
    let flushingNotifications = false;

    function assertActive() {
        if (destroyed) throw new Error(`Restate store "${id}" has been destroyed.`);
    }

    function getEventListeners(type) {
        let listeners = eventListeners.get(type);
        if (!listeners) {
            listeners = new Set();
            eventListeners.set(type, listeners);
        }
        return listeners;
    }

    function emit(type, details = {}) {
        const event = Object.freeze({
            type,
            store: eventTarget ?? publicStore,
            value: stateValue,
            previousValue: stateValue,
            timestamp: Date.now(),
            ...details,
        });
        const listeners = eventListeners.get(type);
        const wildcardListeners = eventListeners.get('*');
        let firstError;

        if (listeners) {
            try {
                callListeners(listeners, [event]);
            } catch (error) {
                firstError ??= error;
            }
        }
        if (wildcardListeners) {
            try {
                callListeners(wildcardListeners, [event]);
            } catch (error) {
                firstError ??= error;
            }
        }

        if (firstError) throw firstError;
        return event;
    }

    function publishState(previousValue, nextValue, action, details) {
        const event = Object.freeze({
            type: 'change',
            action,
            store: eventTarget ?? publicStore,
            value: nextValue,
            previousValue,
            revision,
            timestamp: Date.now(),
            ...details,
        });
        notificationQueue.push(event);
        if (flushingNotifications) return;

        flushingNotifications = true;
        let firstError;
        let queueIndex = 0;

        try {
            while (queueIndex < notificationQueue.length) {
                const queuedEvent = notificationQueue[queueIndex++];
                try {
                    callListeners(subscribers, [
                        queuedEvent.value,
                        queuedEvent.previousValue,
                        queuedEvent,
                    ]);
                } catch (error) {
                    firstError ??= error;
                }

                const changeListeners = eventListeners.get('change');
                if (changeListeners) {
                    try {
                        callListeners(changeListeners, [queuedEvent]);
                    } catch (error) {
                        firstError ??= error;
                    }
                }

                const wildcardListeners = eventListeners.get('*');
                if (wildcardListeners) {
                    try {
                        callListeners(wildcardListeners, [queuedEvent]);
                    } catch (error) {
                        firstError ??= error;
                    }
                }
            }
        } finally {
            notificationQueue.length = 0;
            flushingNotifications = false;
        }

        if (firstError) throw firstError;
    }

    function replace(update, action = 'set', details = {}) {
        assertActive();
        const previousValue = stateValue;
        const nextValue = typeof update === 'function' ? update(previousValue) : update;
        const changed = !sameValue(previousValue, nextValue);

        if (changed) {
            stateValue = nextValue;
            revision += 1;
            publishState(previousValue, nextValue, action, details);
        }

        if (action !== 'set') {
            emit(action, {
                value: stateValue,
                previousValue,
                changed,
                revision,
                ...details,
            });
        }

        return stateValue;
    }

    function set(update) {
        replace(update, 'set');
    }

    function reset() {
        return replace(initialValue, 'reset');
    }

    function shouldRunUpdater() {
        return manuallyStarted || subscribers.size > 0;
    }

    function startUpdaterIfNeeded() {
        if (
            updaterRunning ||
            !shouldRunUpdater() ||
            typeof updater !== 'function' ||
            destroyed
        ) return;

        updaterRunning = true;
        try {
            const cleanup = updater({
                getState: () => stateValue,
                setState: set,
                value: stateValue,
            });
            updaterCleanup = typeof cleanup === 'function' ? cleanup : undefined;
            emit('start');
        } catch (error) {
            updaterRunning = false;
            updaterCleanup = undefined;
            throw error;
        }
    }

    function stopUpdaterIfNeeded(force = false) {
        if (!updaterRunning || (!force && shouldRunUpdater())) return;

        updaterRunning = false;
        const cleanup = updaterCleanup;
        updaterCleanup = undefined;
        cleanup?.();
        if (!destroyed) emit('stop');
    }

    function subscribe(listener) {
        assertActive();
        if (typeof listener !== 'function') {
            throw new TypeError('The subscriber must be a function.');
        }

        subscribers.add(listener);
        try {
            startUpdaterIfNeeded();
        } catch (error) {
            subscribers.delete(listener);
            throw error;
        }

        let active = true;
        return () => {
            if (!active) return;
            active = false;
            subscribers.delete(listener);
            stopUpdaterIfNeeded();
        };
    }

    function on(type, listener) {
        assertActive();
        if (typeof type !== 'string' || type.length === 0) {
            throw new TypeError('The event type must be a non-empty string.');
        }
        if (typeof listener !== 'function') {
            throw new TypeError('The event listener must be a function.');
        }

        const listeners = getEventListeners(type);
        listeners.add(listener);
        let active = true;
        return () => {
            if (!active) return;
            active = false;
            listeners.delete(listener);
            if (listeners.size === 0) eventListeners.delete(type);
        };
    }

    function start() {
        assertActive();
        manuallyStarted = true;
        startUpdaterIfNeeded();
    }

    function stop() {
        if (destroyed) return;
        manuallyStarted = false;
        stopUpdaterIfNeeded();
    }

    function destroy() {
        if (destroyed) return;
        destroyed = true;
        manuallyStarted = false;
        stopUpdaterIfNeeded(true);
        subscribers.clear();
        eventListeners.clear();
        for (const listener of Array.from(destroyListeners)) listener();
        destroyListeners.clear();
    }

    const publicStore = {
        id,
        get: () => stateValue,
        getInitial: () => initialValue,
        getRevision: () => revision,
        isDestroyed: () => destroyed,
        isRunning: () => updaterRunning,
        listenerCount: (type) => type == null
            ? subscribers.size
            : (eventListeners.get(type)?.size ?? 0),
        set,
        reset,
        replace,
        subscribe,
        on,
        emit,
        start,
        stop,
        destroy,
        addDestroyListener(listener) {
            destroyListeners.add(listener);
            return () => destroyListeners.delete(listener);
        },
        setEventTarget(target) {
            eventTarget = target;
        },
    };

    return publicStore;
}
